import React from 'react';
import { StudentDemandSummaryAnalytics } from '../../../types';
import {
  Users,
  Building2,
  CalendarDays,
  TrendingUp,
  MapPin,
  Sparkles,
} from 'lucide-react';

interface DemandKpisProps {
  summary: StudentDemandSummaryAnalytics;
}

export const DemandKpis: React.FC<DemandKpisProps> = ({ summary }) => {
  return (
    <div className="demand-kpi-grid">
      {/* 1. Total Active Students */}
      <div className="demand-kpi-card highlight">
        <div className="demand-kpi-top">
          <span className="demand-kpi-label">إجمالي رغبات الطلاب النشطة</span>
          <div className="demand-kpi-icon-wrap blue">
            <Users size={19} />
          </div>
        </div>
        <div>
          <div className="demand-kpi-value-row">
            <span className="demand-kpi-num">{summary.activeDemandStudents.toLocaleString('ar-EG')}</span>
            <span className="demand-kpi-unit">طالب نشط</span>
          </div>
          <span className="demand-kpi-subtitle">
            من إجمالي {summary.totalDemandStudents.toLocaleString('ar-EG')} مسجل في قاعدة البيانات
          </span>
        </div>
      </div>

      {/* 2. Universities Requested */}
      <div className="demand-kpi-card">
        <div className="demand-kpi-top">
          <span className="demand-kpi-label">الجامعات المستهدفة</span>
          <div className="demand-kpi-icon-wrap purple">
            <Building2 size={19} />
          </div>
        </div>
        <div>
          <div className="demand-kpi-value-row">
            <span className="demand-kpi-num">{summary.uniqueUniversitiesRequested.toLocaleString('ar-EG')}</span>
            <span className="demand-kpi-unit">جامعة وكلية</span>
          </div>
          <span className="demand-kpi-subtitle">
            {summary.highestDemandUniversity
              ? `الأعلى طلباً: ${summary.highestDemandUniversity.universityName}`
              : 'توزيع جامعي متعدد'}
          </span>
        </div>
      </div>

      {/* 3. Total Attendance Demand (Student-Days/Month) */}
      <div className="demand-kpi-card">
        <div className="demand-kpi-top">
          <span className="demand-kpi-label">حجم الطلب الشهري (أيام الحضور)</span>
          <div className="demand-kpi-icon-wrap green">
            <CalendarDays size={19} />
          </div>
        </div>
        <div>
          <div className="demand-kpi-value-row">
            <span className="demand-kpi-num">{summary.totalAttendanceDemandDays.toLocaleString('ar-EG')}</span>
            <span className="demand-kpi-unit">يوم-طالب / شهر</span>
          </div>
          <span className="demand-kpi-subtitle">
            مؤشر الإشغال الفعلي للأسطول والنقل الدوري
          </span>
        </div>
      </div>

      {/* 4. Average Attendance Days */}
      <div className="demand-kpi-card">
        <div className="demand-kpi-top">
          <span className="demand-kpi-label">متوسط الحضور للطالب</span>
          <div className="demand-kpi-icon-wrap amber">
            <TrendingUp size={19} />
          </div>
        </div>
        <div>
          <div className="demand-kpi-value-row">
            <span className="demand-kpi-num">{summary.avgAttendanceDays.toLocaleString('ar-EG')}</span>
            <span className="demand-kpi-unit">يوم شهرياً</span>
          </div>
          <span className="demand-kpi-subtitle">
            معيار التردد والانتقال بين الإسكندرية والجامعات
          </span>
        </div>
      </div>

      {/* 5. Highest Demand Area */}
      <div className="demand-kpi-card">
        <div className="demand-kpi-top">
          <span className="demand-kpi-label">أعلى المناطق السكنية طلباً</span>
          <div className="demand-kpi-icon-wrap indigo">
            <MapPin size={19} />
          </div>
        </div>
        <div>
          <div className="demand-kpi-value-row">
            <span className="demand-kpi-num" style={{ fontSize: '1.35rem' }}>
              {summary.highestDemandArea ? summary.highestDemandArea.areaName : 'سموحة'}
            </span>
            <span className="demand-kpi-unit">
              ({(summary.highestDemandArea?.count || 0).toLocaleString('ar-EG')} طالب)
            </span>
          </div>
          <span className="demand-kpi-subtitle">
            نقطة تجمع رئيسية مقترحة للحافلات (50 راكباً)
          </span>
        </div>
      </div>

      {/* 6. High Opportunity Routes */}
      <div className="demand-kpi-card opportunity">
        <div className="demand-kpi-top">
          <span className="demand-kpi-label">فرص تدشين الخطوط المؤهلة</span>
          <div className="demand-kpi-icon-wrap teal">
            <Sparkles size={19} />
          </div>
        </div>
        <div>
          <div className="demand-kpi-value-row">
            <span className="demand-kpi-num">{summary.highOpportunityRoutesCount.toLocaleString('ar-EG')}</span>
            <span className="demand-kpi-unit">جامعة مؤهلة للتشغيل</span>
          </div>
          <span className="demand-kpi-subtitle">
            تجاوزت الحد الأدنى للركاب (10+ طلاب)
          </span>
        </div>
      </div>
    </div>
  );
};
